import React, { FormEvent, useState } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { Heading } from '@tink/core/src/components/atoms/Heading/Heading';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { RadioButton } from '@tink/core/src/components/atoms/RadioButton/RadioButton';
import { TextField } from '@tink/core/src/components/atoms/TextField';
import { ScreenWrapper } from '../../atoms/ScreenWrapper/ScreenWrapper';
import { Footer } from '../../atoms/Footer/Footer';
import { Header } from '../../organisms/Header/Header';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { InteractionEvent, PageView, UserInteractionEvent } from '../../../models/analytics';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { AppState } from '../../../stores/redux-store';
import { Button } from '../../atoms/Button/Button';
import { messages } from './messages';

type ReasonOption = {
  value: string;
  label: MessageDescriptor;
};

const REASONS: ReasonOption[] = [
  {
    value: 'unsecure',
    label: messages.reasonOne,
  },
  {
    value: 'credentials-issue',
    label: messages.reasonTwo,
  },
  {
    value: 'credentials-forgot',
    label: messages.reasonThree,
  },
  {
    value: 'too-complicated',
    label: messages.reasonFour,
  },
  {
    value: 'dont-have-online-banking',
    label: messages.reasonFive,
  },
];

// Fisher–Yates shuffle
export function shuffle<T>(input: T[]) {
  const output = [...input];
  for (let i = output.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [output[i], output[j]] = [output[j], output[i]];
  }

  return output;
}

const randomizedReasons = shuffle(REASONS);

export type UserFeedbackProps = PropsFromRedux & {
  onSubmit: () => void;
  onSkip: () => void;
  onBack: () => void;
  sendInteractionEvent?: (interactionEvent: InteractionEvent) => void;
};

export const UserFeedbackPresenter: React.FC<UserFeedbackProps> = ({
  onSubmit,
  onSkip,
  onBack,
  clientName,
  sendInteractionEvent: trackEvent,
}) => {
  const { tinklinkAnalytics } = useAnalytics();
  const sendInteractionEvent =
    trackEvent || tinklinkAnalytics.sendInteractionEvent.bind(tinklinkAnalytics);
  const [selectedReason, setSelectedReason] = useState('');
  const [userFeedback, setUserFeedback] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    sendInteractionEvent({
      view: PageView.EXIT_FEEDBACK_SCREEN,
      action: `${PageView.EXIT_FEEDBACK_SCREEN}/${UserInteractionEvent.PRIMARY_BUTTON}`,
      label: userFeedback,
    });

    onSubmit();
  };

  return (
    <ScreenWrapper>
      <Header onBack={onBack} addBottomMargin={false} pageView={PageView.EXIT_FEEDBACK_SCREEN} />
      <ContentBody>
        <Box as="form" id="feedback-form" onSubmit={handleSubmit} pt="large">
          <Heading as="h2" variant="h4" mb="medium">
            <FormattedMessage {...messages.heading} />
          </Heading>
          {randomizedReasons.map((reason) => (
            <Box mb="normedium" key={reason.value}>
              <RadioButton
                key={reason.value}
                value={reason.value}
                name="reason"
                checked={reason.value === selectedReason}
                onChange={() => {
                  setSelectedReason(reason.value);

                  sendInteractionEvent({
                    view: PageView.EXIT_FEEDBACK_SCREEN,
                    action: `${PageView.EXIT_FEEDBACK_SCREEN}/${UserInteractionEvent.INPUT_CHANGE}`,
                    label: reason.value,
                  });
                }}
              >
                <FormattedMessage {...reason.label} values={{ clientName }} />
              </RadioButton>
            </Box>
          ))}
          <TextField
            label={<FormattedMessage {...messages.feedbackLabel} />}
            id="feedback"
            name="feedback"
            inputProps={{ maxLength: 500 }}
            value={userFeedback}
            multiline
            minRows={3}
            fullWidth
            onChange={(e) => {
              const userFeedbackText = e.target.value;
              setUserFeedback(userFeedbackText);
            }}
          />
        </Box>
        <Footer stickToBottom>
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            form="feedback-form"
            data-testid="btn-submit"
            disabled={selectedReason === '' && userFeedback === ''}
          >
            <FormattedMessage {...messages.submit} />
          </Button>
          <Button
            type="reset"
            onClick={() => {
              onSkip();

              sendInteractionEvent({
                view: PageView.EXIT_FEEDBACK_SCREEN,
                action: `${PageView.EXIT_FEEDBACK_SCREEN}/${UserInteractionEvent.CLOSE_BUTTON}`,
              });
            }}
          >
            <FormattedMessage {...messages.skip} />
          </Button>
        </Footer>
      </ContentBody>
    </ScreenWrapper>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    clientName: state.oauth.description?.clientName,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const UserFeedback = connector(UserFeedbackPresenter);
